import React, {FC, PropsWithChildren, useRef} from 'react';
import {useDragDropManager} from 'react-dnd';
import {connect} from 'react-redux';

import {globalWindow} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {safeJsonStringify} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {getHasDragAndDropLogging} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {makeMapStateToProps} from '../../../srcCommon/helpers/reselectHelpers';

/*
 * Props.
 */

interface InboxesDragDropLoggerProps {
  hasDragAndDropLogging: boolean;
}

/*
 * Component.
 */

const DRAG_STUCK_DELAY = 15 * 1000;

const InboxesDragDropLoggerComponent: FC<PropsWithChildren<InboxesDragDropLoggerProps>> = (props) => {
  const dndManager = useDragDropManager();
  const isDraggingRef = useRef(false);
  const timeoutId = useRef<number | null>(null);

  const clearDragTimeout = () => {
    if (timeoutId.current) {
      globalWindow.clearTimeout(timeoutId.current);
    }

    timeoutId.current = null;
  };

  useInitialEffect(() => {
    if (!props.hasDragAndDropLogging) {
      return () => {};
    }

    const unsubscribe = dndManager.getMonitor().subscribeToStateChange(() => {
      const monitor = dndManager.getMonitor();
      const registry = dndManager.getRegistry();
      const isDragging = monitor.isDragging();
      const sourceId = monitor.getSourceId();

      if (isDragging && !timeoutId.current) {
        timeoutId.current = globalWindow.setInstrumentedTimeout(() => {
          // Try to grab the DND source to gather more data
          const source = sourceId && registry.getSource(sourceId);
          logger.warn('Drag and drop operation did not see end of dragging after 15s', {
            dnd_item_type: String(monitor.getItemType()),
            dnd_item: JSON.stringify(monitor.getItem()),
            dnd_extra: (source && safeJsonStringify(source)) || '',
            is_dragging: isDraggingRef.current,
          });
          clearDragTimeout();
        }, DRAG_STUCK_DELAY);
      } else if (!isDragging && timeoutId.current) {
        clearDragTimeout();
      }
    });

    const handleDragStart = () => {
      isDraggingRef.current = true;
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      const monitor = dndManager.getMonitor();
      if (monitor.isDragging()) {
        const sourceId = monitor.getSourceId();
        const source = sourceId && dndManager.getRegistry().getSource(sourceId);
        logger.warn('Drag and drop operation ended without updating react-dnd monitor', {
          dnd_item_type: String(monitor.getItemType()),
          dnd_item: JSON.stringify(monitor.getItem()),
          dnd_extra: (source && safeJsonStringify(source)) || '',
        });
      }
    };

    globalWindow.addEventListener('dragstart', handleDragStart, true);
    globalWindow.addEventListener('dragend', handleDragEnd, true);

    return () => {
      unsubscribe();
      globalWindow.removeEventListener('dragstart', handleDragStart);
      globalWindow.removeEventListener('dragend', handleDragEnd);
    };
  });

  return <>{props.children}</>;
};

const mapStateToProps = makeMapStateToProps()(() => ({
  hasDragAndDropLogging: getHasDragAndDropLogging,
}));

export const InboxesDragDropLogger = connect(mapStateToProps)(InboxesDragDropLoggerComponent);
